import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import CheckboxInputListItem from 'LuxKit/List/CheckboxInputListItem'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import BodyText from 'LuxKit/Typography/BodyText'

const ListContainer = styled.div`
  margin-inline: ${rem(-16)};
`

const RatingBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.palette.product.exclusive.background};
  color: white;
  border-radius: ${rem(4)};
  font-size: ${rem(12)};
  font-weight: 600;
  padding: ${rem(2)} ${rem(6)};
  min-width: ${rem(28)};
`

const RATING_OPTIONS = [
  { value: 9, label: '9.0' },
  { value: 8, label: '8.0' },
  { value: 7, label: '7.0' },
]

interface Props {
  ratingCounts?: Record<number, number>
}

function ExperienceSearchRatingFilter({ ratingCounts }: Props) {
  const intl = useIntl()
  const { searchFilters, setSearchFilters } = useExperiencesSearchFilters()

  const onRatingChange = useCallback((rating: number | undefined) => {
    setSearchFilters({
      ...searchFilters,
      minRating: rating,
    })
  }, [searchFilters, setSearchFilters])

  const isAllSelected = !searchFilters.minRating

  const allLabel = useMemo(() => intl.formatMessage({
    id: '4AnY0LVHfJOfBTaRm8Sz',
    defaultMessage: 'All',
    description: 'Checkbox label to select all filter options in the filters panel',
  }), [intl])

  const orAboveLabel = useMemo(() => intl.formatMessage({
    id: '_u_PESnw7GIdW8U9jJ7Z',
    defaultMessage: 'or above',
    description: 'Suffix text displayed with each minimum rating option in experience search filters',
  }), [intl])

  return (
    <ListContainer>
      <CheckboxInputListItem
        inputPlacement="start"
        checked={isAllSelected}
        onChange={() => onRatingChange(undefined)}
        title={allLabel}
        size="medium"
      />
      {RATING_OPTIONS.map(option => (
        <CheckboxInputListItem
          key={option.value}
          inputPlacement="start"
          checked={searchFilters.minRating === option.value}
          onChange={() => onRatingChange(searchFilters.minRating === option.value ? undefined : option.value)}
          title={<><RatingBadge>{option.label}</RatingBadge> &nbsp;&nbsp;{orAboveLabel}</>}
          size="medium"
        >
          {ratingCounts?.[option.value] !== undefined && (
            <BodyText variant="medium" colour="neutral-two">
              {intl.formatMessage({ id: '9yD0hkvxAp8fVybO4oUK', defaultMessage: '({count})', description: 'Count of matching experiences shown next to a rating filter option' }, { count: ratingCounts[option.value] })}
            </BodyText>
          )}
        </CheckboxInputListItem>
      ))}
    </ListContainer>
  )
}

export default ExperienceSearchRatingFilter
